import {
  ProductType,
  ProductCoverType,
  ProductPaymentPeriod,
  DecisionType,
} from './types';

const mapProductType = (s) => {
  switch (s) {
    case 'life':
      return ProductType.LifeProtection;
    case 'ccic':
      return ProductType.ChildrensCriticalIllnessProtection;
    case 'ci':
      return ProductType.CriticalIllnessProtection;
    case 'lifeEssentials':
      return ProductType.LifeEssentials;
    case 'ip':
      return ProductType.IncomeProtection;
    case 'aci':
      return ProductType.CombinedLifeAndCriticalIllnessProtection;
    case 'ciEssentials':
      return ProductType.CriticalIllnessEssentials;
    case 'aciEssentials':
      return ProductType.CombinedLifeAndCriticalIllnessEssentials;
    case 'fracture':
      return ProductType.FracturePlusProtection;
    default:
      return s;
  }
};

const mapCoverType = (s) => {
  switch ((s || '').toLowerCase()) {
    case 'level':
      return ProductCoverType.Level;
    case 'increasing':
      return ProductCoverType.Increasing;
    case 'decreasing':
      return ProductCoverType.Decreasing;
    case 'family':
      return ProductCoverType.Family;
    case 'family income benefit':
      return ProductCoverType.Family;
    default:
      return s;
  }
};

const mapPaymentPeriod = (s) => {
  switch (s) {
    case 'Full':
      return ProductPaymentPeriod.FullTerm;
    default:
      return s;
  }
};

const mapStatus = (s) => (s === 'In force' ? 'In force' : s);

const normaliseCover = (c) => {
  const common = {
    productType: mapProductType(c.productType),
    productName: c.productName,
    coverType: mapCoverType(c.coverType),
    monthlyPremium: c.monthlyPremium,
    coverReference: c.coverReference,
    decision: DecisionType.STANDARD,
    amount: c.amount,
    startDate: c.startDate,
    endDate: c.endDate,
    coverUpgradeLink: c.coverUpgradeLink ?? '',
    termsAndConditions: c.termsAndConditions ?? [],
    disabled: c.disabled,
    quotationDate: c.quotationDate,
    status: mapStatus(c.status),
  };

  switch (common.productType) {
    case ProductType.LifeProtection: {
      const totalPct = (c.beneficiaries ?? [])
        .map((b) => b.percentageAllocated ?? 0)
        .reduce((a, b) => a + b, 0);

      return {
        ...common,
        productType: ProductType.LifeProtection,
        beneficiariesTotalPercentage: totalPct,
        isInTrust: !!c.isInTrust,
        beneficiaries: (c.beneficiaries ?? []).map((b) => ({
          firstName: b.firstName,
          lastName: b.lastName,
          dateOfBirth: b.dateOfBirth ?? '',
          percentageAllocated: b.percentageAllocated ?? 0,
        })),
      };
    }

    case ProductType.ChildrensCriticalIllnessProtection: {
      const totalPct = (c.beneficiaries ?? [])
        .map((b) => b.percentageAllocated ?? 0)
        .reduce((a, b) => a + b, 0);

      return {
        ...common,
        productType: ProductType.ChildrensCriticalIllnessProtection,
        beneficiariesTotalPercentage: totalPct,
        isInTrust: !!c.isInTrust,
        beneficiaries: (c.beneficiaries ?? []).map((b) => ({
          firstName: b.firstName,
          lastName: b.lastName,
          dateOfBirth: b.dateOfBirth ?? '',
          percentageAllocated: b.percentageAllocated ?? 0,
        })),
      };
    }

    case ProductType.IncomeProtection:
      return {
        ...common,
        productType: ProductType.IncomeProtection,
        deferredPeriod: c.deferredPeriod,
        paymentPeriod:
          mapPaymentPeriod(c.paymentPeriod) ?? ProductPaymentPeriod.FullTerm,
      };

    case ProductType.CriticalIllnessProtection:
      return {
        ...common,
        productType: ProductType.CriticalIllnessProtection,
      };

    case ProductType.CriticalIllnessEssentials:
      return {
        ...common,
        productType: ProductType.CriticalIllnessEssentials,
        isTpdIncluded: c.isTpdIncluded,
      };

    case ProductType.LifeEssentials: {
      const totalPct = (c.beneficiaries ?? [])
        .map((b) => b.percentageAllocated ?? 0)
        .reduce((a, b) => a + b, 0);

      return {
        ...common,
        productType: ProductType.LifeEssentials,
        beneficiariesTotalPercentage: totalPct,
        isInTrust: !!c.isInTrust,
        beneficiaries: (c.beneficiaries ?? []).map((b) => ({
          firstName: b.firstName,
          lastName: b.lastName,
          dateOfBirth: b.dateOfBirth ?? '',
          percentageAllocated: b.percentageAllocated ?? 0,
        })),
      };
    }

    case ProductType.CombinedLifeAndCriticalIllnessProtection: {
      const totalPct = (c.beneficiaries ?? [])
        .map((b) => b.percentageAllocated ?? 0)
        .reduce((a, b) => a + b, 0);

      return {
        ...common,
        productType: ProductType.CombinedLifeAndCriticalIllnessProtection,
        beneficiariesTotalPercentage: totalPct,
        isInTrust: !!c.isInTrust,
        beneficiaries: (c.beneficiaries ?? []).map((b) => ({
          firstName: b.firstName,
          lastName: b.lastName,
          dateOfBirth: b.dateOfBirth ?? '',
          percentageAllocated: b.percentageAllocated ?? 0,
        })),
      };
    }

    case ProductType.CombinedLifeAndCriticalIllnessEssentials: {
      const totalPct = (c.beneficiaries ?? [])
        .map((b) => b.percentageAllocated ?? 0)
        .reduce((a, b) => a + b, 0);

      return {
        ...common,
        productType: ProductType.CombinedLifeAndCriticalIllnessEssentials,
        beneficiariesTotalPercentage: totalPct,
        isInTrust: !!c.isInTrust,
        isTpdIncluded: c.isTpdIncluded,
        beneficiaries: (c.beneficiaries ?? []).map((b) => ({
          firstName: b.firstName,
          lastName: b.lastName,
          dateOfBirth: b.dateOfBirth ?? '',
          percentageAllocated: b.percentageAllocated ?? 0,
        })),
      };
    }

    case ProductType.FracturePlusProtection:
      return {
        ...common,
        productType: ProductType.FracturePlusProtection,
      };

    default:
      return {
        ...common,
        productType: ProductType.LifeProtection,
        beneficiariesTotalPercentage: 0,
        isInTrust: false,
        beneficiaries: [],
      };
  }
};

export const normaliseApi = (api) => {
  return (api.data ?? []).map((p) => {
    const covers = (p.covers ?? []).map(normaliseCover);
    const earliestCoverStart =
      covers
        .map((c) => c.startDate)
        .sort((a, b) => +new Date(a) - +new Date(b))[0] || '';

    return {
      fullName: p.fullName,
      policyNumber: p.policyNumber,
      totalMonthlyPremium: p.totalMonthlyPremium,
      additionalPolicyDocuments: p.additionalPolicyDocuments,
      hasPolicyDataErrors: p.hasPolicyDataErrors,
      hasPolicyDocumentErrors: p.hasPolicyDocumentErrors,
      startDate: earliestCoverStart,
      covers,
    };
  });
};
